/**
 * Lift2Fit — the lifter's Manage coach page (`#/my-coach`).
 *
 * Everything the lifter's coaching relationship needs, in one lifter-shell
 * surface: the linked coach and invites (Coach), the coach's diary in list or
 * calendar form (Booking) and the lifter's own block goals (Goals). It replaces
 * the coach + injury blocks that used to live on `#/profile`; the profile keeps
 * identity, units, export and sign out.
 *
 * 0028 IA: there is no Chat sub-tab. Talking to a coach is the lifter's single
 * primary action here, so it is the page's one orange button and it opens the
 * standalone `#/chat?side=lifter` workspace in its own window — the same
 * workspace the coach uses. Keeping the tab would have been a second way in.
 *
 * A coach-role account viewing their own training sees exactly this page in the
 * lifter shell — `#/my-coach` is a lifter route, never part of `#/coach`/`#/crm`.
 */
import { useState } from 'react';
import { useSession } from '@/lib/useSession';
import { ScreenHeader, StatusChip } from '../components';
import { useCoaching } from '../coach';
import { GoalsManager } from '../screens/GoalsScreen';
import { BookingTab } from './BookingTab';
import { CoachTab } from './CoachTab';
import { DiscoverTab } from './DiscoverTab';

type TabId = 'coach' | 'discover' | 'booking' | 'goals';

interface TabDef {
  id: TabId;
  label: string;
}

const TABS: ReadonlyArray<TabDef> = [
  { id: 'coach', label: 'Coach' },
  { id: 'discover', label: 'Discover' },
  { id: 'booking', label: 'Booking' },
  { id: 'goals', label: 'Goals' },
];

export function MyCoachScreen({ initialTab = 'coach' }: { initialTab?: TabId } = {}) {
  const session = useSession();
  const coaching = useCoaching();
  const [tab, setTab] = useState<TabId>(initialTab);

  const link = coaching.link;
  const linked = link?.status === 'active' && link.coach !== null;
  const coachName = link?.coach?.displayName ?? 'your coach';

  return (
    <div className="flex flex-col gap-6" data-testid="my-coach">
      <ScreenHeader
        eyebrow="Your training"
        title="Manage coach"
        badge={linked ? <StatusChip tone="success">Coach linked</StatusChip> : <StatusChip tone="neutral">No coach</StatusChip>}
        lead={
          linked
            ? `${coachName} can read the fields you share and prescribe your programme. Manage the link, book sessions and set goals here — your conversation opens from the chat button in the top bar.`
            : 'Connect a human coach, book their sessions and set your own block goals — your conversation opens from the chat button in the top bar.'
        }
      />

      <div className="flex flex-col gap-4">
        <nav
          role="tablist"
          aria-label="Manage coach sections"
          className="flex flex-wrap gap-1 border-b border-hairline"
          data-testid="my-coach-tabs"
        >
          {TABS.map((entry) => {
            const active = tab === entry.id;
            return (
              <button
                key={entry.id}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={`my-coach-panel-${entry.id}`}
                id={`my-coach-tab-${entry.id}`}
                data-testid={`my-coach-tab-${entry.id}`}
                onClick={() => setTab(entry.id)}
                className={
                  'inline-flex h-11 items-center gap-2 border-b-2 px-3 text-[12px] font-bold uppercase tracking-[0.08em] transition-colors duration-150 ' +
                  (active ? 'border-accent text-ink' : 'border-transparent text-ink-muted hover:text-ink')
                }
              >
                {entry.label}
              </button>
            );
          })}
        </nav>

        <div
          role="tabpanel"
          id={`my-coach-panel-${tab}`}
          aria-labelledby={`my-coach-tab-${tab}`}
          data-testid={`my-coach-panel-${tab}`}
        >
          {tab === 'coach' ? <CoachTab coaching={coaching} /> : null}
          {tab === 'discover' ? <DiscoverTab /> : null}
          {tab === 'booking' ? <BookingTab coachName={coachName} linked={linked} /> : null}
          {tab === 'goals' ? (
            <div data-testid="my-coach-goals-tab">
              <GoalsManager />
            </div>
          ) : null}
        </div>
      </div>

      {session.isDemo ? (
        <p className="text-[12px] text-ink-muted">
          Demo mode — coaching reads and writes need the API. The banner above says which dataset you are on.
        </p>
      ) : null}
    </div>
  );
}
